import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Tabs, TabsList, TabsTrigger } from "../../../../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../../components/ui/table";
import { Button } from "../../../../components/ui/button";
import { Badge } from "../../../../components/ui/badge";
import { getCouponsByStatus, updateCouponStatus, getCoinPackageTemplates, deleteDraftCoupon, duplicateCoupon } from '../../../../services/couponAdminApi';
import { useToast } from "../../../../hooks/use-toast";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../../../components/ui/tooltip";
import { Edit, Ban, CheckCircle, History, Copy, Trash2, Plus, AlertTriangle } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { Skeleton } from "../../../../components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../../components/ui/alert-dialog";

const statusMap = {
  active: 'ACTIVE',
  draft: 'DRAFT',
  expired: 'EXPIRED',
  archived: 'ARCHIVED',
};

const TABLE_HEADERS = (
  <TableHeader>
    <TableRow>
      <TableHead className="w-[20%]">Coupon Details</TableHead>
      <TableHead>Effect</TableHead>
      <TableHead>Targeting</TableHead>
      <TableHead>Limits & Expiry</TableHead>
      <TableHead className="text-right">Actions</TableHead>
    </TableRow>
  </TableHeader>
);

const ViewCoupons = () => {
  const { status } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [coupons, setCoupons] = useState([]);
  const [packageTemplates, setPackageTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [couponToDelete, setCouponToDelete] = useState(null);

  const packageIdToNameMap = useMemo(() => {
    return packageTemplates.reduce((acc, pkg) => {
      acc[pkg.id] = pkg.name;
      return acc;
    }, {});
  }, [packageTemplates]);

  const { displayCoupons, staleCoupons } = useMemo(() => {
    if (status !== 'active') {
      return { displayCoupons: coupons, staleCoupons: [] };
    }
    const now = new Date();
    const display = [];
    const stale = [];
    for (const coupon of coupons) {
      if (coupon.expiry_date && new Date(coupon.expiry_date) < now) {
        stale.push(coupon);
      } else {
        display.push(coupon);
      }
    }
    return { displayCoupons: display, staleCoupons: stale };
  }, [coupons, status]);

  const fetchAllData = (currentApiStatus) => {
    setLoading(true);
    Promise.all([
      getCouponsByStatus(currentApiStatus),
      getCoinPackageTemplates()
    ])
    .then(([couponsRes, packagesRes]) => {
      setCoupons(couponsRes.data);
      const allPackages = [...(packagesRes.data.FREE || []), ...(packagesRes.data.PAID || [])];
      setPackageTemplates(allPackages);
    })
    .catch(err => {
      console.error("Failed to fetch data:", err);
      toast({ variant: "destructive", title: "Error", description: "Could not fetch required data." });
    })
    .finally(() => setLoading(false));
  };

  useEffect(() => {
    const apiStatus = statusMap[status] || 'ACTIVE';
    fetchAllData(apiStatus);
  }, [status]);

  const handleStatusChange = (couponId, newStatus) => {
    updateCouponStatus(couponId, newStatus)
      .then(() => {
        toast({ title: "Success", description: `Coupon status updated to ${newStatus}.` });
        fetchAllData(statusMap[status] || 'ACTIVE');
      })
      .catch(err => {
        toast({
          variant: "destructive",
          title: "Error",
          description: `Failed to update status: ${err.response?.data?.detail || err.message}`,
        });
      });
  };

  const handleDuplicate = (couponId) => {
    duplicateCoupon(couponId)
      .then((res) => {
        const newCoupon = res.data;
        toast({ title: "Coupon Duplicated", description: `Now editing the new draft: ${newCoupon.name}` });
        navigate(`/admin/payments/edit/${newCoupon.id}`);
      })
      .catch(err => {
        toast({ variant: "destructive", title: "Error", description: `Failed to duplicate coupon: ${err.response?.data?.detail || err.message}`});
      });
  };

  const openDeleteDialog = (coupon) => {
    setCouponToDelete(coupon);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (!couponToDelete) return;

    deleteDraftCoupon(couponToDelete.id)
      .then(() => {
        toast({ title: "Success", description: `Draft coupon "${couponToDelete.code}" has been permanently deleted.` });
        fetchAllData(statusMap[status] || 'ACTIVE');
      })
      .catch(err => {
        toast({
          variant: "destructive",
          title: "Error",
          description: `Failed to delete draft: ${err.response?.data?.detail || err.message}`,
        });
      })
      .finally(() => {
        setIsDeleteDialogOpen(false);
        setCouponToDelete(null);
      });
  };

  const renderExpiry = (coupon) => {
    if (coupon.expiry_date) {
      const expiryDate = new Date(coupon.expiry_date);
      const isPast = expiryDate < new Date();
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger>
              <span className={isPast ? 'text-red-600' : ''}>{format(expiryDate, "MMM d, yyyy")}</span>
            </TooltipTrigger>
            <TooltipContent>
              <p>{isPast ? 'Expired' : 'Expires'} {formatDistanceToNow(expiryDate, { addSuffix: true })}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }
    if (coupon.relative_expiry_days) {
      return `${coupon.relative_expiry_days} days after activation`;
    }
    return <span className="text-muted-foreground">No Expiry</span>;
  };

  const renderTargeting = (coupon) => {
    const packageIds = coupon.conditions?.applicable_package_ids;
    const sellerStatus = coupon.conditions?.target_is_paid_seller;

    const applicabilityBadge = coupon.applicability_type === 'SPECIFIC_SELLERS'
      ? <Badge variant="outline" className="border-amber-500 text-amber-700">Activation Required</Badge>
      : <Badge variant="secondary">All Sellers</Badge>;

    let sellerBadge;
    if (sellerStatus === true) sellerBadge = <Badge variant="outline">Paid Only</Badge>;
    else if (sellerStatus === false) sellerBadge = <Badge variant="outline">Free Only</Badge>;
    else sellerBadge = null;

    let packageBadge;
    if (!packageIds || packageIds.length === 0) {
        packageBadge = <Badge variant="secondary">All Packages</Badge>;
    } else {
        const packageNames = packageIds.map(id => packageIdToNameMap[id] || `ID:${id}`);
        const totalPackages = packageNames.length;
        const displayNames = packageNames.slice(0, 3);
        const remainingCount = totalPackages - displayNames.length;

        packageBadge = (
            <Tooltip>
              <TooltipTrigger>
                <Badge variant="outline" className="cursor-help">
                  {totalPackages} Specific Package(s)
                </Badge>
              </TooltipTrigger>
              <TooltipContent className="z-50">
                <div className="p-2">
                  <p className="font-semibold mb-2 text-sm">Applies to:</p>
                  <ul className="list-disc pl-4 space-y-1">
                    {displayNames.map(name => <li key={name}>{name}</li>)}
                    {remainingCount > 0 && (
                      <li className="text-muted-foreground">...and {remainingCount} more</li>
                    )}
                  </ul>
                </div>
              </TooltipContent>
            </Tooltip>
        );
    }

    const ageBadge = coupon.min_seller_age_days
      ? <Badge variant="outline">Min. age: {coupon.min_seller_age_days}d</Badge>
      : null;

    return <div className="flex flex-col items-start space-y-1">{applicabilityBadge}{sellerBadge}{ageBadge}{packageBadge}</div>;
  };

  const renderCouponRow = (coupon) => (
    <TableRow key={coupon.id}>
      <TableCell>
        <div className="font-medium">{coupon.name}</div>
        <div className="text-sm text-muted-foreground">{coupon.code}</div>
      </TableCell>
      <TableCell>
        <div className="flex items-center">
            <Badge variant="default">{coupon.coupon_type.replace(/_/g, ' ')}</Badge>
        </div>
        <div className="text-sm font-bold mt-1">
            {coupon.percentage ? `${coupon.percentage}%` : ''}
            {coupon.value ? (coupon.coupon_type === 'VALUE_DISCOUNT' ? `₹${coupon.value}`: `${coupon.value} units`) : ''}
        </div>
      </TableCell>
      <TableCell>{renderTargeting(coupon)}</TableCell>
      <TableCell>
        <div>{coupon.max_redemptions_per_user} per user</div>
        <div className="text-sm text-muted-foreground">{coupon.max_redemptions_total ? `${coupon.max_redemptions_total} total` : 'Unlimited total'}</div>
        <div className="mt-1 text-xs">{renderExpiry(coupon)}</div>
      </TableCell>
      <TableCell className="text-right space-x-2">
        <Button variant="ghost" size="sm" onClick={() => handleDuplicate(coupon.id)}><Copy className="mr-2 h-4 w-4"/> Copy</Button>

        {coupon.status === 'DRAFT' && (
          <>
            <Button variant="outline" size="sm" onClick={() => navigate(`/admin/payments/edit/${coupon.id}`)}><Edit className="mr-2 h-4 w-4"/> Edit</Button>
            <Button variant="default" size="sm" onClick={() => handleStatusChange(coupon.id, 'ACTIVE')}><CheckCircle className="mr-2 h-4 w-4"/> Publish</Button>
            <Button variant="destructive" size="sm" onClick={() => openDeleteDialog(coupon)}><Trash2 className="mr-2 h-4 w-4"/> Delete</Button>
          </>
        )}

        {coupon.status === 'ACTIVE' && (
           <Button variant="destructive" size="sm" onClick={() => handleStatusChange(coupon.id, 'EXPIRED')}><Ban className="mr-2 h-4 w-4"/> Expire</Button>
        )}

         {coupon.status === 'EXPIRED' && (
           <>
             <Button variant="outline" size="sm" onClick={() => navigate(`/admin/payments/edit/${coupon.id}`)}><Edit className="mr-2 h-4 w-4"/> Edit</Button>
             <Button variant="secondary" size="sm" onClick={() => handleStatusChange(coupon.id, 'ACTIVE')}><History className="mr-2 h-4 w-4"/> Re-activate</Button>
           </>
        )}
      </TableCell>
    </TableRow>
  );

  const statusLabel = status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Active';

  return (
    <TooltipProvider>
    <div>
      <div className="flex items-center justify-between mb-4">
        <Tabs value={status} onValueChange={(tab) => navigate(`/admin/payments/view/${tab}`)}>
          <TabsList>
            <TabsTrigger value="active" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Active</TabsTrigger>
            <TabsTrigger value="draft" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Drafts</TabsTrigger>
            <TabsTrigger value="expired" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Expired</TabsTrigger>
            <TabsTrigger value="archived" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Archived</TabsTrigger>
          </TabsList>
        </Tabs>
        <Button onClick={() => navigate('/admin/payments/create')}>
          <Plus className="mr-2 h-4 w-4" /> Create Coupon
        </Button>
      </div>

      <div className="border rounded-lg">
        {loading ? (
          <Table>
            {TABLE_HEADERS}
            <TableBody>
              {[1, 2, 3].map(i => (
                <TableRow key={i}>
                  <TableCell><Skeleton className="h-4 w-32 mb-2" /><Skeleton className="h-3 w-24" /></TableCell>
                  <TableCell><Skeleton className="h-6 w-28 mb-2" /><Skeleton className="h-3 w-16" /></TableCell>
                  <TableCell><Skeleton className="h-6 w-24 mb-2" /><Skeleton className="h-6 w-28" /></TableCell>
                  <TableCell><Skeleton className="h-3 w-20 mb-1" /><Skeleton className="h-3 w-24 mb-1" /><Skeleton className="h-3 w-20" /></TableCell>
                  <TableCell className="text-right"><Skeleton className="h-8 w-24 ml-auto" /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : displayCoupons.length === 0 ? (
          <Table>
            {TABLE_HEADERS}
            <TableBody>
              <TableRow>
                <TableCell colSpan={5} className="text-center py-10 text-muted-foreground">
                  No {statusLabel.toLowerCase()} coupons found.
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        ) : (
          <Table>
            {TABLE_HEADERS}
            <TableBody>
              {displayCoupons.map(renderCouponRow)}
            </TableBody>
          </Table>
        )}
      </div>

      {staleCoupons.length > 0 && (
        <div className="mt-6 border border-yellow-300 bg-yellow-50 dark:bg-yellow-950/20 dark:border-yellow-700 rounded-lg">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-yellow-300 dark:border-yellow-700">
            <AlertTriangle className="h-4 w-4 text-yellow-600" />
            <span className="text-sm font-medium text-yellow-800 dark:text-yellow-400">
              {staleCoupons.length} coupon{staleCoupons.length > 1 ? 's' : ''} past expiry date — still marked as Active
            </span>
          </div>
          <Table>
            {TABLE_HEADERS}
            <TableBody>
              {staleCoupons.map(coupon => (
                <TableRow key={coupon.id}>
                  <TableCell>
                    <div className="font-medium">{coupon.name}</div>
                    <div className="text-sm text-muted-foreground">{coupon.code}</div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center">
                        <Badge variant="default">{coupon.coupon_type.replace(/_/g, ' ')}</Badge>
                    </div>
                    <div className="text-sm font-bold mt-1">
                        {coupon.percentage ? `${coupon.percentage}%` : ''}
                        {coupon.value ? (coupon.coupon_type === 'VALUE_DISCOUNT' ? `₹${coupon.value}`: `${coupon.value} units`) : ''}
                    </div>
                  </TableCell>
                  <TableCell>{renderTargeting(coupon)}</TableCell>
                  <TableCell>
                    <div>{coupon.max_redemptions_per_user} per user</div>
                    <div className="text-sm text-muted-foreground">{coupon.max_redemptions_total ? `${coupon.max_redemptions_total} total` : 'Unlimited total'}</div>
                    <div className="mt-1 text-xs">{renderExpiry(coupon)}</div>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="destructive" size="sm" onClick={() => handleStatusChange(coupon.id, 'EXPIRED')}>
                      <Ban className="mr-2 h-4 w-4"/> Mark Expired
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the draft coupon
              <span className="font-bold mx-1">"{couponToDelete?.code}"</span>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm} className="bg-red-600 hover:bg-red-700">Continue</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
    </TooltipProvider>
  );
};

export default ViewCoupons;
